<template>
    <div id="aon-app" ref="captureRef" class="display-flex flex-wrap w-100 position-relative">
        <div class="main-content w-100 h-100 position-relative">
            <div v-if="isAuthenticated" class="header-container w-100" id="header-container">
                <ArmoryHeader :nav-links="headerNavLinks">
                    <template #headerImage>
                        <img :src="logoImport" alt="Moat Logo" style="width: 160px" />
                    </template>
                    <template #rightAppend>
                        <div class="right-holder display-flex align-items-center">
                            <GlobalSearch
                                class="mr-md"
                                style="min-width: 400px"
                                ignore-enter
                                enable-click-outside
                            />
                            <ArmoryDropdownBasic
                                v-model="selectedHeaderLink"
                                :options="profileOptions"
                                dropdown-pos="left"
                                @change="handleDropdownSelect"
                            >
                                <template #trigger="{ toggleDropdownEmit }">
                                    <div
                                        class="avatar p-sm bg-moat-800 border-radius-lg display-flex align-items-center justify-center cursor-pointer"
                                        style="width: 40px; height: 40px"
                                        @click="toggleDropdownEmit"
                                    >
                                        <p class="text-moat-100">{{ userInitials }}</p>
                                    </div>
                                </template>
                            </ArmoryDropdownBasic>
                        </div>
                    </template>
                </ArmoryHeader>
            </div>
            <PatentList v-if="patentViewerStore.showPatentViewerList" />
            <PatentDetails
                v-show="patentViewerStore.patentDetailsOpen && route.name !== 'Research Patent'"
                :hide-mapped-products="patentViewerStore.hideMappedProducts"
            />
            <router-view />
        </div>
        <AppFooter v-if="isAuthenticated" />
        <Snackbar />
        <AonModal
            v-if="showGlobalModal"
            :title="globalModalObj.title"
            message=""
            col-width="4"
            @close="closeModal()"
        >
            <div class="modal-content">
                <div v-if="globalModalObj.definition" class="definition mb-md">
                    <h6 class="bold text-primary-darker mb-sm">Definition</h6>
                    <p>{{ globalModalObj.definition }}</p>
                </div>
                <div v-if="globalModalObj.moreInfo" class="more-info mb-md">
                    <h6 class="bold text-primary-darker mb-sm">Why this is important</h6>
                    <p>{{ globalModalObj.moreInfo }}</p>
                </div>
                <div v-if="globalModalObj.zScoreInfo" class="score-info">
                    <h6 class="bold text-primary-darker mb-sm">What is a clipped z-score</h6>
                    <p>{{ globalModalObj.zScoreInfo }}</p>
                </div>
            </div>
        </AonModal>
        <EditWatchlistModal v-if="datasetToEdit" />
        <CompanySummary />
        <WatchlistAddOrCreateModal v-if="idsToAdd || datasetStore.showOnlyCreateWatchlists" />
    </div>
</template>

<script setup>
import { ref, computed, watch, inject, onBeforeMount, onMounted } from 'vue'
import { RouterView, useRoute, useRouter } from 'vue-router'
import {
    useAppStore,
    useScreenerStore,
    useDatasetStore,
    useEntityStore,
    usePatentViewerStore,
} from '@/stores'

import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { hideAllPoppers } from 'floating-vue'
import logoImport from '@/assets/moat_alpha.png'

import { ArmoryHeader, ArmoryDropdownBasic } from '@moatmetrics/armory/src/components'
import GlobalSearch from '@/components/GlobalSearch'
import AppFooter from './components/AppFooter'
import Snackbar from './components/Snackbar'

import WatchlistAddOrCreateModal from '@/components/Watchlists/WatchlistActions/WatchlistAddOrCreateModal.vue'
import EditWatchlistModal from '@/components/Watchlists/WatchlistActions/EditWatchlistModal.vue'
import CompanySummary from '@/components/helpers/CompanySummary.vue'
import PatentList from '@/components/patentViewer/PatentList.vue'
import PatentDetails from '@/components/patentViewer/PatentDetails.vue'

import { useFlag, useFlagsStatus, useUnleashContext } from '@unleash/proxy-client-vue'
import { useAuth } from '@/auth/authPlugin'

const updateUnleashContext = useUnleashContext()
const { t } = useI18n()

const datasetStore = useDatasetStore()
const { idsToAdd, datasetToEdit } = storeToRefs(datasetStore)

const appStore = useAppStore()
const { showGlobalModal, globalModalObj, showTOS } = storeToRefs(appStore)
const { user, isAuthenticated, logout } = useAuth()

const entityStore = useEntityStore()
const patentViewerStore = usePatentViewerStore()
const router = useRouter()
const route = useRoute()
const captureRef = ref(null)
const rumAgent = inject('rumAgent')
const isProUser = useFlag('ipAlpha.proUsers')
const showPatentSearch = useFlag('ipAlpha.patentSearch')
const portfolioFeatureWhitelist = useFlag('ipAlpha.portfolioFeaturesWhitelist')
const allowAllFeatures = useFlag('ipAlpha.godAccess')

const profileOptions = ref([
    {
        label: 'My Arenas',
        value: 'arenas',
        iconOptions: {
            iconName: 'fa-swords',
        },
    },
    {
        label: 'My Watchlists',
        value: 'watchlists',
        iconOptions: {
            iconName: 'fa-eye',
        },
    },
    {
        label: 'My Reports',
        value: 'reports',
        iconOptions: {
            iconName: 'fa-file-alt',
        },
    },
    {
        label: 'Get Help',
        value: 'help',
        iconOptions: {
            iconName: 'fa-question-circle',
        },
    },
    {
        label: 'Sign Out',
        value: 'signout',
        iconOptions: {
            iconName: 'fa-arrow-right-from-bracket',
        },
    },
])

const allowPortfolioManager = computed(() => {
    // If user does not have an entity_pk, do not show the portfolio manager button
    if (!user.value?.entity_pk || portfolioFeatureWhitelist.value) {
        return false
    }

    return (
        (isProUser.value && user.value?.entity_pk) ||
        (allowAllFeatures.value && user.value?.entity_pk) // god mode
    )
})

const selectedHeaderLink = ref(null)
const headerNavLinks = computed(() => {
    // if (!isAuthenticated.value || !user) return []

    const links = []

    // Only add Portfolio Manager link if user has a valid entity_pk
    if (user.value?.entity_pk) {
        links.push({
            linkLabel: 'Portfolio Manager',
            routeObject: {
                name: 'Research Company',
                params: { targetPk: user.value.entity_pk },
                query: { tabName: 'portfolioManager' },
            },
            hide: allowPortfolioManager.value,
        })
    }

    // Add other navigation links
    links.push(
        {
            linkLabel: 'Playbooks',
            routeObject: {
                name: 'Playbooks',
            },
        },
        {
            linkLabel: 'Competitive Arena',
            routeObject: {
                name: 'Arena',
            },
        },
        {
            linkLabel: 'Company Screener',
            routeObject: {
                name: 'Companies',
            },
        }
    )

    if (showPatentSearch.value) {
        links.push({
            linkLabel: 'Patent Search',
            routeObject: {
                name: 'Patent Search',
            },
        })
    }

    return links
})

const entityName = computed(() => {
    return entityStore.entity ? entityStore.entity.name : ''
})

const userInitials = computed(() => {
    if (user.value?.given_name) {
        return user.value?.given_name?.charAt(0) + user.value?.family_name?.charAt(0)
    } else {
        return user.value?.email?.charAt(0) + user.value?.email?.charAt(1)
    }
})

watch(
    () => router,
    (newVal) => {
        if (newVal.currentRoute.value.meta.tabName) {
            document.title = `Alpha ${newVal.currentRoute.value.meta.tabName ? '- ' + newVal.currentRoute.value.meta.tabName : ''}`
        }
    },
    { flush: 'pre', immediate: true, deep: true }
)

watch(
    () => entityName.value,
    (newVal) => {
        document.title = `Alpha - ${newVal}`
    }
)

watch(showTOS, (val) => {
    if (val) {
        hideAllPoppers()
    }
})

watch(user, async () => {
    if (user.value?.id) {
        await setUserContexts()
    }
})

watch(
    () => patentViewerStore.showPatentViewerList,
    (isOpen) => {
        if (isOpen) {
            document.documentElement.style.position = 'fixed'
        } else {
            document.documentElement.style.position = 'static'
        }
    }
)

onBeforeMount(async () => {
    await setUserContexts()
})

onMounted(() => {
    const leftNav = document.querySelector('.left-nav')
    if (leftNav) {
        leftNav.id = 'left-nav'
    }

    const headerContainer = document.querySelector('.header-container')
    if (headerContainer) {
        const aonDropdown = headerContainer.querySelector('.aon-dropdown')
        if (aonDropdown) {
            aonDropdown.id = 'menu'
        }
    }
})

const setUserContexts = async () => {
    if (!user.value?.id) return

    await updateUnleashContext({
        userId: user.value.email,
    })

    rumAgent.setUser({
        id: user.value.id,
        email: user.value.email,
        tenant_id: user.value.org_id,
        tenant_name: user.value.org_name,
    })
}

const handleDropdownSelect = async (params) => {
    if (params.value === 'help') {
        window.open(`mailto:${t('headerNav.support.email')}`)
    }

    if (params.value === 'arenas') {
        router.push({ name: 'Arena List View' })
    }
    if (params.value === 'watchlists') {
        router.push({ name: 'Watchlist List' })
    }
    if (params.value === 'reports') {
        router.push({ name: 'Reports' })
    }

    if (params.value === 'signout') {
        await logout({
            async openUrl() {
                window.location.href = document.location.origin
            },
        })
    }
}

const closeModal = () => {
    appStore.setShowGlobalModal(false)
}
</script>

<style lang="scss">
@use '@moatmetrics/armory/src/sass/main.scss';
@use '@moatmetrics/armory/src/sass/responsive.scss' as responsive;
@use '@/styles/projectMain.scss';

.port-manager-button {
    .a-button {
        border-color: var(--color-border-default);
    }
    .a-button-label {
        color: var(--color-text-secondary);
        font-weight: 400;
    }
}

a {
    cursor: pointer;
}

.overflow-hide {
    overflow: hidden;
}

.dropdown-menu-holder {
    border: var(--border-width-thin) solid var(--color-border-default);
    border-radius: var(--border-radius-md);
    cursor: pointer;
    position: relative;

    &:hover {
        .aon-dropdown {
            background: var(--color-moat-800) !important;

            .dropdown-icon {
                background: transparent !important;

                svg {
                    color: var(--color-stone-200) !important;
                }
            }

            li {
                &:hover {
                    background: var(--color-moat-800) !important;
                }
            }
        }
    }

    p {
        position: relative;
        pointer-events: none;
        z-index: 1;
        color: var(--color-stone-0) !important;
    }

    .aon-dropdown {
        display: block !important;
        height: 48px !important;
        position: absolute !important;
        left: -4px;
        top: 4px;
        border-radius: var(--border-radius-md);
        transition: all 0.3s ease-in-out;

        .dropdown-icon {
            border-radius: 0 !important;
        }

        &.open {
            background: var(--color-moat-800) !important;

            .dropdown-icon {
                background: transparent !important;
            }
        }

        .items {
            border: var(--border-width-thin) solid var(--color-border-default);
            box-shadow: 0px 6px 15px 0px rgba(0, 17, 53, 0.2);
        }
    }
}

.aon-container {
    max-width: none;

    @include responsive.overSized {
        max-width: none;
    }
}

#aon-app {
    .main-content {
        min-height: calc(100vh - 190px); // height of footer
        background: var(--color-background-light);
        z-index: 1;
    }
}

// REVAMP VIA ARMORY - BB
.header-hook {
    .container {
        padding-right: 20px !important;
        padding-left: 20px !important;
    }
    .open {
        .dropdown-icon {
            background: rgba(var(--color-text-secondary), 0.3);
            color: var(--color-text-secondary) !important;
        }

        .main-items {
            transform: translateY(10px) !important;
        }
    }
    .main-items {
        ul {
            overflow-y: auto !important;
            max-height: 250px !important;
        }

        .items {
            max-height: 230px !important;
        }
    }
}

.moat-application-header .table-aips-latest {
    position: relative;
    z-index: 0;
}

.feedback {
    position: fixed;
    z-index: 10010;
    bottom: 30px;
    right: 30px;
}
</style>
